import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, ModalProps } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { ModalBox } from '../../../../../components/modal/ModalBox';
import { ClassTeam } from '../ClassesTag/ClassesTag';
import { User } from '../../../../../types/tournament';
import { Ticket, TicketRoleEnum } from '../../../../../types/Ticket';
import {
  captainAfterTicketTransfer,
  isTeamRegistrationRole,
} from '../../../../../utils/teamRosterForm';
import { generateTeamName } from '../../../../../utils/generateTeamName';
import { getDisplayName } from '../../../../../utils/userDisplayName';
import AsyncSelect from 'react-select/async';
import { StylesConfig } from 'react-select';
import { imgOptionUserLabel } from '../../../../../utils/imgOptionUserLabel';
import { fetchUsers } from '../../../../../features/users/actionCreators';
import { addUsersFromSearch } from '../../../../../features/users/userSlice';
import debounce from 'lodash/debounce';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refundTicket, revokeTicket, transferTicket } from '../../../../../apis/ticket';
import { fetchUsersData } from '../../../../../apis/tournament';
import { Notification } from '../../../../../hooks/useNotification';
import { ClassTypeEnum, TypeError } from '../../../../../types/enums';
import { selectTournament } from '../../../../../features/tournament/tournamentSelectors';
import { RoundedAvatar } from '../../../../../components';

type RemoveAction = 'refund_and_remove' | 'transfer_ticket' | 'remove_only';

type RefundPreset = 'ticket_only' | 'ticket_plus_reg' | 'custom';

export const RemovePlayerModal = (props: ModalProps) => {
  const { t } = useTranslation();
  const prefix = 'removePlayerModal-';

  const {
    title = t(prefix + 'title'),
    team,
    user,
    teams,
    setField,
    type,
    tickets,
    classId,
  }: {
    title?: string;
    team: ClassTeam;
    user: User;
    teams: ClassTeam[];
    setField: (field: string, value: any, shouldValidate?: boolean) => void;
    type: any;
    tickets: Ticket[] | undefined;
    classId: number | string;
  } = useAppSelector((state) => state.modals.data);

  const [action, setAction] = useState<RemoveAction | null>(null);
  const [replacementUserId, setReplacementUserId] = useState<string>('');
  const [refundReason, setRefundReason] = useState<string>('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundPreset, setRefundPreset] = useState<RefundPreset>('ticket_only');
  const [notifyUser, setNotifyUser] = useState<boolean>(true);
  const { data } = useAppSelector(selectTournament);

  const inputPrefix = 'input-';

  const { userData } = useAppSelector((state) => state.users);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const teamUsers = useMemo(() => team.users, [team.users]);

  const allPlayersInClass = useMemo(() => {
    const users: User[] = [];
    teams.forEach((t) => {
      t.users.forEach((u) => {
        if (!users.find((ex) => ex.id === u.id)) {
          users.push(u);
        }
      });
    });
    // Exclude the current user being removed
    return users.filter((u) => u.id !== user.id);
  }, [teams, user.id]);

  const excludedUserIds = useMemo(
    () => new Set(allPlayersInClass.map((u) => u.id).concat(user.id)),
    [allPlayersInClass, user.id]
  );

  const customSelectStyles: StylesConfig = {
    control: (base) => ({ ...base, borderRadius: '16px', minHeight: '50px' }),
  };

  const userTicket = useMemo(() => {
    return tickets?.find((t) => t.teamId === (team?.id as number) && t.user?.id === user?.id);
  }, [tickets, team?.id, user?.id]);

  const isTeamLeague = type?.value === ClassTypeEnum.TEAMS;

  const isTeamRegistrationTicket = useMemo(() => {
    if (!isTeamLeague || !userTicket) return false;
    return isTeamRegistrationRole(userTicket.ticketRole);
  }, [isTeamLeague, userTicket]);

  const isRefundableTicket = useMemo(() => {
    return Boolean(
      userTicket?.paymentId &&
        (userTicket.paymentId.startsWith('pi_') ||
          userTicket.paymentId.startsWith('TRANSFERRED_FROM_'))
    );
  }, [userTicket]);

  // If the ticket is from a transferred ticket, we travel back to the original ticket to get the payment info
  const paidTicket = useMemo(() => {
    let existingTicket = userTicket;
    while (existingTicket?.paymentId?.startsWith('TRANSFERRED_FROM_')) {
      const parentId = existingTicket?.paymentId.split('_')[2];
      existingTicket = tickets?.find((t) => t.id === Number(parentId));
    }
    return existingTicket;
  }, [userTicket, tickets]);

  const serviceFeePercent = useMemo(() => {
    const pct = data?.serviceFee ?? 0;
    return Number.isFinite(pct) ? pct : 0;
  }, [data?.serviceFee]);

  const serviceFeeAmount = useMemo(() => {
    if (!paidTicket) return undefined;
    const amount = (Number(paidTicket.price || 0) * serviceFeePercent) / 100;
    return Number.isFinite(amount) ? amount : undefined;
  }, [paidTicket, serviceFeePercent]);

  // Keep refund amount in sync when preset is not custom
  useEffect(() => {
    if (!paidTicket) return;
    if (refundPreset === 'custom') return;
    const ticketPrice = Number(paidTicket.price || 0);
    const nonRefundableServiceFee = Number(serviceFeeAmount || 0);
    const registrationFee = Number(data?.registrationFee || 0);

    let amount = 0;
    if (refundPreset === 'ticket_only') {
      amount = ticketPrice - nonRefundableServiceFee;
    } else if (refundPreset === 'ticket_plus_reg') {
      amount = ticketPrice - nonRefundableServiceFee + registrationFee;
    }

    if (amount < 0) amount = 0;
    setRefundAmount(amount.toFixed(2));
  }, [refundPreset, serviceFeeAmount, data?.registrationFee, paidTicket]);

  const refundMutation = useMutation({
    mutationFn: ({
      ticketId,
      reason,
      amount,
      notifyUser,
    }: {
      ticketId: string;
      reason?: string;
      amount?: number;
      notifyUser?: boolean;
    }) => refundTicket(Number(ticketId), reason, amount, notifyUser),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', classId] });
      Notification(
        TypeError.success,
        t(prefix + 'ticketRefunded', { amount: `${data.refundCurrency} ${data.refundAmount}` })
      );
    },
    onError: () => Notification(TypeError.error, t(prefix + 'ticketRefundFailed')),
  });

  const transferMutation = useMutation({
    mutationFn: ({
      ticketId,
      newUserId,
      notifyUser,
    }: {
      ticketId: string;
      newUserId: string;
      notifyUser?: boolean;
    }) => transferTicket(Number(ticketId), Number(newUserId), notifyUser),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', classId] });
      Notification(TypeError.success, t(prefix + 'ticketTransferred'));
    },
    onError: () => Notification(TypeError.error, t(prefix + 'ticketTransferFailed')),
  });

  const revokeMutation = useMutation({
    mutationFn: ({ ticketId, notifyUser }: { ticketId: string; notifyUser?: boolean }) =>
      revokeTicket(Number(ticketId), notifyUser),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', classId] });
      Notification(
        TypeError.success,
        t(prefix + (isTeamRegistrationTicket ? 'teamWithdrawn' : 'ticketRevoked'))
      );
    },
    onError: () => Notification(TypeError.error, t(prefix + 'ticketRevokeFailed')),
  });

  const removeInFlight =
    refundMutation.isPending || transferMutation.isPending || revokeMutation.isPending;

  const handleSave = useCallback(async () => {
    if (!action || removeInFlight) return;

    const wasCaptain = team.captain?.[0]?.id === user.id;
    const isTeamsEvent = type?.value === ClassTypeEnum.TEAMS;

    let updatedTeams = teams;

    if (action === 'transfer_ticket' && replacementUserId) {
      const resolvedReplacement = userData.find(
        (u) => u.id.toString() === replacementUserId || u.id === Number(replacementUserId)
      );
      if (!resolvedReplacement) {
        Notification(TypeError.error, t(prefix + 'ticketTransferFailed'));
        return;
      }

      // Mirror API planRegistrationHolderTransfer: remove source, add recipient once.
      // Captain moves when the source held the registration ticket, or was already captain.
      const withoutSource = teamUsers.filter((u) => u.id !== user.id);
      const uniqueUsers = [...new Map(withoutSource.map((u) => [u.id, u])).values()];
      const transferUsers = uniqueUsers.some((u) => u.id === resolvedReplacement.id)
        ? uniqueUsers
        : [...uniqueUsers, resolvedReplacement];

      updatedTeams = teams.map((t) => {
        if (t.id !== team.id) {
          return t;
        }
        return {
          ...t,
          users: transferUsers,
          name: generateTeamName(transferUsers, type, team),
          captain: captainAfterTicketTransfer({
            isTeamsEvent,
            isTeamRegistrationTicket,
            wasCaptain,
            currentCaptain: t.captain,
            replacement: resolvedReplacement,
          }),
        };
      });
    } else {
      const updatedUsers = teamUsers.filter((u) => u.id !== user.id);
      const nextCaptain = (() => {
        if (!wasCaptain) {
          return team.captain ?? [];
        }
        if (!isTeamsEvent) {
          return [];
        }
        const registrationHolder = updatedUsers.find((u) => {
          const ticket = tickets?.find(
            (t) =>
              t.teamId === (team?.id as number) &&
              t.user?.id === u.id &&
              (t.ticketRole === TicketRoleEnum.REGISTRATION || t.ticketRole == null)
          );
          return !!ticket;
        });
        if (registrationHolder) {
          return [registrationHolder];
        }
        return updatedUsers[0] ? [updatedUsers[0]] : [];
      })();

      updatedTeams = teams.map((t) => {
        if (t.id !== team.id) {
          return t;
        }
        return {
          ...t,
          users: updatedUsers,
          name: generateTeamName(updatedUsers, type, team),
          ...(wasCaptain ? { captain: nextCaptain } : {}),
        };
      });

      if (
        (action === 'remove_only' || action === 'refund_and_remove') &&
        isTeamRegistrationTicket
      ) {
        updatedTeams = updatedTeams.filter((t) => t.id !== team.id);
      } else if (
        (action === 'remove_only' || action === 'refund_and_remove') &&
        updatedUsers.length === 0
      ) {
        updatedTeams = updatedTeams.filter((t) => t.id !== team.id);
      }
    }

    try {
      if (action === 'refund_and_remove' && userTicket && isRefundableTicket) {
        const parsedAmount =
          refundAmount.trim() === '' ? undefined : Number.parseFloat(refundAmount);
        const amount =
          typeof parsedAmount === 'number' && Number.isFinite(parsedAmount)
            ? parsedAmount
            : undefined;
        await refundMutation.mutateAsync({
          ticketId: userTicket.id.toString(),
          reason: refundReason || undefined,
          amount,
          notifyUser,
        });
      } else if (action === 'transfer_ticket' && userTicket && replacementUserId) {
        await transferMutation.mutateAsync({
          ticketId: userTicket.id.toString(),
          newUserId: replacementUserId,
          notifyUser,
        });
      } else if (action === 'remove_only' && userTicket) {
        await revokeMutation.mutateAsync({ ticketId: userTicket.id.toString(), notifyUser });
      }

      setField('teams', updatedTeams);
      props.onHide?.();
    } catch (e) {
      console.error(e);
      return;
    }
  }, [
    action,
    isRefundableTicket,
    isTeamRegistrationTicket,
    notifyUser,
    props,
    removeInFlight,
    refundMutation,
    refundReason,
    refundAmount,
    replacementUserId,
    revokeMutation,
    setField,
    t,
    team,
    teamUsers,
    teams,
    tickets,
    transferMutation,
    type,
    user.id,
    userData,
    userTicket,
  ]);

  const promiseOptions = debounce((inputValue, cb) => {
    fetchUsersData(inputValue)
      .then((response) => {
        dispatch(addUsersFromSearch(response));
        cb(response.map((user: User) => imgOptionUserLabel(user)));
      })
      .catch((e) => {
        console.log(e);
      });
  }, 500);

  const availableUsers = useMemo(
    () => userData.filter((u) => !excludedUserIds.has(u.id)),
    [userData, excludedUserIds]
  );

  return (
    <ModalBox
      {...props}
      title={title}
      handleSave={handleSave}
      isDisabled={
        action === null || (action === 'transfer_ticket' && !replacementUserId) || removeInFlight
      }
      withoutMinHeight
      btnText={t(prefix + 'confirm')}
    >
      <div className="d-flex flex-column gap-3">
        <div className="mb-2">
          <strong>
            {getDisplayName(user)}
          </strong>{' '}
          — {team.name}
          {paidTicket && paidTicket?.id !== userTicket?.id && (
            <div>
              {t(prefix + 'transferredFrom')}:{' '}
              <strong>{getDisplayName(paidTicket.user)}</strong>
            </div>
          )}
          {paidTicket?.price && paidTicket?.price > 0 && (
            <div className="text-muted mt-1">
              <div>
                {t(prefix + 'ticketPrice')}:{' '}
                {`${paidTicket.currency} ${Number(paidTicket.price || 0).toFixed(2)}`}
              </div>
              {data?.registrationFee !== null && data?.registrationFee !== undefined && (
                <div>
                  {t(prefix + 'registrationFee')}:{' '}
                  {`${paidTicket?.currency ? paidTicket.currency + ' ' : ''}${Number(
                    data?.registrationFee || 0
                  ).toFixed(2)}`}
                </div>
              )}
              {serviceFeeAmount !== undefined && serviceFeePercent > 0 && paidTicket && (
                <div>
                  {t(prefix + 'serviceFee')}:{' '}
                  {`${paidTicket.currency} ${serviceFeeAmount.toFixed(2)}`} (
                  {serviceFeePercent.toFixed(2)}%)
                  <span className="ms-1">{t(prefix + 'nonRefundable')}</span>
                </div>
              )}
            </div>
          )}
        </div>
        {isTeamRegistrationTicket && (
          <Alert variant="warning" className="mb-0">
            {t(prefix + 'teamRegistrationWithdrawWarning')}
          </Alert>
        )}
        <div className="d-flex flex-column gap-2">
          <div className="btn-group" role="group" aria-label="Remove player actions">
            <Button
              variant={action === 'refund_and_remove' ? 'dark' : 'outline-dark'}
              className="w-100"
              disabled={!isRefundableTicket}
              onClick={() => {
                if (isRefundableTicket) {
                  setAction('refund_and_remove');
                }
              }}
            >
              {t(prefix + (isTeamRegistrationTicket ? 'refundAndRemoveTeam' : 'refundAndRemove'))}
            </Button>
          </div>
          {action === 'refund_and_remove' && isRefundableTicket && (
            <div className="mt-2">
              <Form.Label>{t(prefix + 'reason')}</Form.Label>
              <Form.Select value={refundReason} onChange={(e) => setRefundReason(e.target.value)}>
                <option value="">{t(prefix + 'reasonOtherUnspecified')}</option>
                <option value="duplicate">{t(prefix + 'reasonDuplicate')}</option>
                <option value="fraudulent">{t(prefix + 'reasonFraudulent')}</option>
                <option value="requested_by_customer">
                  {t(prefix + 'reasonRequestedByCustomer')}
                </option>
              </Form.Select>
              <div className="mt-2 d-flex flex-column mb-4">
                <div className="d-flex flex-column">
                  <Form.Label>{t(prefix + 'refundAmount')}</Form.Label>
                  <div className="btn-group mb-2" role="group" aria-label="Refund presets">
                    <Button
                      variant={refundPreset === 'ticket_only' ? 'dark' : 'outline-dark'}
                      onClick={() => setRefundPreset('ticket_only')}
                    >
                      {t(prefix + 'ticketOnly')}
                    </Button>
                    <Button
                      variant={refundPreset === 'ticket_plus_reg' ? 'dark' : 'outline-dark'}
                      onClick={() => setRefundPreset('ticket_plus_reg')}
                    >
                      {t(prefix + 'ticketPlusRegistration')}
                    </Button>
                    <Button
                      variant={refundPreset === 'custom' ? 'dark' : 'outline-dark'}
                      onClick={() => setRefundPreset('custom')}
                    >
                      {t(prefix + 'customAmount')}
                    </Button>
                  </div>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={refundAmount}
                    disabled={refundPreset !== 'custom'}
                    onChange={(e) => setRefundAmount(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
          {(!isTeamLeague || isTeamRegistrationTicket) && (
            <div className="btn-group" role="group" aria-label="Remove player actions">
              <Button
                variant={action === 'transfer_ticket' ? 'dark' : 'outline-dark'}
                className="w-100"
                onClick={() => setAction('transfer_ticket')}
              >
                {t(prefix + 'transferTicket')}
              </Button>
            </div>
          )}
          {action === 'transfer_ticket' && (!isTeamLeague || isTeamRegistrationTicket) && (
            <div className="mt-2">
              <Form.Label>{t(prefix + 'selectReplacementPlayer')}</Form.Label>
              <AsyncSelect
                styles={customSelectStyles}
                name="replacementUserId"
                className="basic-multi-select holder-select"
                classNamePrefix="select"
                defaultOptions={availableUsers.map((user) => imgOptionUserLabel(user))}
                loadOptions={promiseOptions}
                placeholder={t(inputPrefix + 'placeholderSelect') + '...'}
                onChange={(opt: any) => setReplacementUserId(opt?.value?.toString() || '')}
                formatOptionLabel={(data: any) => (
                  <div className="d-flex align-items-center">
                    <RoundedAvatar avatar={data.avatar} alt="User avatar" name="user" />
                    {data.label}
                  </div>
                )}
              />
            </div>
          )}
          <div className="btn-group" role="group" aria-label="Remove player actions">
            <Button
              variant={action === 'remove_only' ? 'dark' : 'outline-dark'}
              className="w-100"
              onClick={() => setAction('remove_only')}
            >
              {t(
                prefix +
                  (isTeamRegistrationTicket
                    ? 'removeTeamWithoutRefund'
                    : 'removePlayerWithoutRefund')
              )}
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <Form.Check
            type="checkbox"
            id="notify-user-checkbox"
            label={t(prefix + 'notifyUser')}
            checked={notifyUser}
            onChange={(e) => setNotifyUser(e.target.checked)}
          />
          <Form.Text className="text-muted">{t(prefix + 'notifyUserDescription')}</Form.Text>
        </div>
      </div>
    </ModalBox>
  );
};
